import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin, catalog, teamFavorites } from '../api/methods';
import { searchCatalogIndex, groupByCategory, } from '../utils/catalogIndex';
export const catalogKeys = {
    customers: (query) => ['catalog', 'customers', query],
    items: (query, limit) => ['catalog', 'items', query, limit ?? null],
    allItems: ['catalog', 'items', 'all'],
    kits: ['catalog', 'kits'],
    kitDetail: (kitId) => ['catalog', 'kits', kitId],
    employees: (wirelessOnly, commsOnly) => ['catalog', 'employees', wirelessOnly, commsOnly],
    favorites: ['catalog', 'favorites'],
    stats: ['catalog', 'stats'],
    itemPrivate: (qbItemId) => ['catalog', 'item-private', qbItemId],
    priceHistory: (qbItemId) => ['catalog', 'item-price-history', qbItemId],
};
// ── Optimistic catalog-metadata helpers ─────────────────────────────────────
// Local-only item metadata (team favorites, nickname, internal notes) is never
// QBO-owned, so it's safe to patch in place. Patching the cached lists directly
// avoids refetching the ~4,300-row catalog index on every favorite toggle — the
// worst measured offender in the #547 performance audit.
const CATALOG_ITEMS_PREFIX = ['catalog', 'items'];
/** Find a cached Item by id across the index + any search-result lists. */
function findCachedItem(qc, qbItemId) {
    for (const [, list] of qc.getQueriesData({ queryKey: CATALOG_ITEMS_PREFIX })) {
        const found = list?.find((it) => it.qb_item_id === qbItemId);
        if (found)
            return found;
    }
    return undefined;
}
/** Patch one item in every cached catalog-items list (index + searches). */
function patchCachedItem(qc, qbItemId, patch) {
    qc.setQueriesData({ queryKey: CATALOG_ITEMS_PREFIX }, (list) => list?.map((it) => (it.qb_item_id === qbItemId ? { ...it, ...patch } : it)));
}
export function useCustomers(query) {
    return useQuery({
        queryKey: catalogKeys.customers(query),
        queryFn: () => catalog.searchCustomers(query),
        enabled: query !== undefined,
        staleTime: 30 * 1000, // 30s
    });
}
export function useCreateCustomer() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => catalog.createCustomer(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['catalog', 'customers'] });
        },
    });
}
export function useItems(query, options) {
    return useQuery({
        queryKey: catalogKeys.items(query, options?.limit),
        queryFn: () => catalog.searchItems(query, options),
        enabled: query !== undefined,
        staleTime: 30 * 1000,
    });
}
const EMPTY_ITEMS = [];
/**
 * Loads the full catalog once and exposes instant client-side search, a SKU
 * lookup map, and active-only Browse-by-category groups. Raw items and byId
 * retain inactive rows so catalog management and old ticket pricing can resolve
 * them, while picker-facing search/categories keep them unaddable by default.
 */
export function useCatalogIndex(options) {
    // `enabled` lets a consumer defer the (large) full-catalog download until the
    // material add/search UI actually opens — a mobile tech just viewing a ticket
    // shouldn't fetch ~4,300 rows. Cached data still reads through when disabled,
    // and the query key is shared, so any enabled consumer warms it for all.
    const enabled = options?.enabled ?? true;
    const query = useQuery({
        queryKey: catalogKeys.allItems,
        queryFn: () => catalog.listAllItems(),
        enabled,
        staleTime: 10 * 60 * 1000, // 10 min — refreshed lazily; edits invalidate it
        gcTime: 24 * 60 * 60 * 1000, // keep warm across the session
    });
    const items = query.data ?? EMPTY_ITEMS;
    const byId = useMemo(() => {
        const map = new Map();
        for (const item of items)
            map.set(item.qb_item_id, item);
        return map;
    }, [items]);
    const categories = useMemo(() => groupByCategory(items), [items]);
    const search = useCallback((q, limit, inclusionOptions) => searchCatalogIndex(items, q, limit, inclusionOptions), [items]);
    // query.refetch is referentially stable across renders (React Query), so the
    // wrapper object below only changes when data/status actually changes.
    const refetch = query.refetch;
    // Memoize the wrapper object so consumers depending on the whole index
    // (e.g. a useMemo'd search result) don't recompute on every render.
    return useMemo(() => ({
        items,
        byId,
        categories,
        search,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error,
        refetch,
    }), [items, byId, categories, search, query.isLoading, query.isFetching, query.isError, query.error, refetch]);
}
export function useKits() {
    return useQuery({
        queryKey: catalogKeys.kits,
        queryFn: () => catalog.getKits(),
        staleTime: 5 * 60 * 1000, // 5 min
    });
}
export function useKitDetail(kitId) {
    return useQuery({
        queryKey: catalogKeys.kitDetail(kitId ?? ''),
        queryFn: () => catalog.getKitDetail(kitId),
        enabled: kitId !== null,
        staleTime: 5 * 60 * 1000,
    });
}
export function useEmployees(options) {
    return useQuery({
        queryKey: catalogKeys.employees(options?.wirelessOnly, options?.commsOnly),
        queryFn: () => catalog.getEmployees(options),
        staleTime: 5 * 60 * 1000,
    });
}
export function useTeamFavorites() {
    return useQuery({
        queryKey: catalogKeys.favorites,
        queryFn: () => teamFavorites.get(),
        staleTime: 60 * 1000,
    });
}
export function useAddTeamFavorite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (qbItemId) => teamFavorites.add(qbItemId),
        // Optimistically add to the favorites list + flag the item in the index/search
        // caches, instead of refetching the whole catalog index on every toggle.
        onMutate: async (qbItemId) => {
            await queryClient.cancelQueries({ queryKey: catalogKeys.favorites });
            const prevFavorites = queryClient.getQueryData(catalogKeys.favorites);
            const prevItems = queryClient.getQueriesData({ queryKey: CATALOG_ITEMS_PREFIX });
            const item = findCachedItem(queryClient, qbItemId);
            queryClient.setQueryData(catalogKeys.favorites, (cur) => {
                const list = cur ?? [];
                if (list.some((it) => it.qb_item_id === qbItemId))
                    return list;
                return item ? [...list, { ...item, is_favorite: true }] : list;
            });
            patchCachedItem(queryClient, qbItemId, { is_favorite: true });
            return { prevFavorites, prevItems };
        },
        onError: (_err, _qbItemId, ctx) => {
            if (!ctx)
                return;
            queryClient.setQueryData(catalogKeys.favorites, ctx.prevFavorites);
            for (const [key, data] of ctx.prevItems)
                queryClient.setQueryData(key, data);
        },
        // Reconcile ONLY the favorites list — never the full ~4,300-row index.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
        },
    });
}
export function useRemoveTeamFavorite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (qbItemId) => teamFavorites.remove(qbItemId),
        onMutate: async (qbItemId) => {
            await queryClient.cancelQueries({ queryKey: catalogKeys.favorites });
            const prevFavorites = queryClient.getQueryData(catalogKeys.favorites);
            const prevItems = queryClient.getQueriesData({ queryKey: CATALOG_ITEMS_PREFIX });
            queryClient.setQueryData(catalogKeys.favorites, (cur) => (cur ?? []).filter((it) => it.qb_item_id !== qbItemId));
            patchCachedItem(queryClient, qbItemId, { is_favorite: false });
            return { prevFavorites, prevItems };
        },
        onError: (_err, _qbItemId, ctx) => {
            if (!ctx)
                return;
            queryClient.setQueryData(catalogKeys.favorites, ctx.prevFavorites);
            for (const [key, data] of ctx.prevItems)
                queryClient.setQueryData(key, data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
        },
    });
}
export function useCatalogStats() {
    return useQuery({
        queryKey: catalogKeys.stats,
        queryFn: () => catalog.getStats(),
        staleTime: 60 * 1000,
    });
}
export function useUpdateNickname() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, nickname }) => catalog.updateNickname(qbItemId, nickname),
        // Nickname is local-only metadata — patch it in place instead of refetching
        // the whole catalog index.
        onMutate: async ({ qbItemId, nickname }) => {
            await queryClient.cancelQueries({ queryKey: CATALOG_ITEMS_PREFIX });
            await queryClient.cancelQueries({ queryKey: catalogKeys.favorites });
            const prevItems = queryClient.getQueriesData({ queryKey: CATALOG_ITEMS_PREFIX });
            const prevFavorites = queryClient.getQueryData(catalogKeys.favorites);
            const patch = { nickname: nickname ?? undefined };
            patchCachedItem(queryClient, qbItemId, patch);
            queryClient.setQueryData(catalogKeys.favorites, (list) => list?.map((it) => (it.qb_item_id === qbItemId ? { ...it, ...patch } : it)));
            return { prevItems, prevFavorites };
        },
        onError: (_err, _vars, ctx) => {
            if (!ctx)
                return;
            for (const [key, data] of ctx.prevItems)
                queryClient.setQueryData(key, data);
            queryClient.setQueryData(catalogKeys.favorites, ctx.prevFavorites);
        },
        // Reconcile the favorites list (nickname shows there) + stats (nickname %),
        // never the full index.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
            queryClient.invalidateQueries({ queryKey: catalogKeys.stats });
        },
    });
}
export function useUpdateInternalNotes() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, internalNotes }) => catalog.updateInternalNotes(qbItemId, internalNotes),
        // Internal notes are local-only metadata — patch in place, don't refetch
        // the full catalog index.
        onMutate: async ({ qbItemId, internalNotes }) => {
            await queryClient.cancelQueries({ queryKey: CATALOG_ITEMS_PREFIX });
            await queryClient.cancelQueries({ queryKey: catalogKeys.favorites });
            const prevItems = queryClient.getQueriesData({ queryKey: CATALOG_ITEMS_PREFIX });
            const prevFavorites = queryClient.getQueryData(catalogKeys.favorites);
            const patch = { internal_notes: internalNotes ?? undefined };
            patchCachedItem(queryClient, qbItemId, patch);
            queryClient.setQueryData(catalogKeys.favorites, (list) => list?.map((it) => (it.qb_item_id === qbItemId ? { ...it, ...patch } : it)));
            return { prevItems, prevFavorites };
        },
        onError: (_err, _vars, ctx) => {
            if (!ctx)
                return;
            for (const [key, data] of ctx.prevItems)
                queryClient.setQueryData(key, data);
            queryClient.setQueryData(catalogKeys.favorites, ctx.prevFavorites);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
        },
    });
}
/**
 * Warm the full catalog index in the background (idle-time prefetch). Lets the
 * material adder / estimate builder open with data already in cache instead of
 * paying the ~4,300-row download on first open. Shares the query key with
 * useCatalogIndex, so a warmed fetch serves every consumer.
 */
export function usePrefetchCatalogIndex() {
    const queryClient = useQueryClient();
    return useCallback(() => {
        void queryClient.prefetchQuery({
            queryKey: catalogKeys.allItems,
            queryFn: () => catalog.listAllItems(),
            staleTime: 10 * 60 * 1000,
        });
    }, [queryClient]);
}
/**
 * Alturex-private item data (cost/margin/vendor SKU). Lazily fetched — pass the
 * id only when a detail panel is open AND the user can read it (sales_view:
 * Sales/Manager/Admin — #577).
 */
export function useItemPrivate(qbItemId, enabled = true) {
    return useQuery({
        queryKey: catalogKeys.itemPrivate(qbItemId ?? ''),
        queryFn: () => catalog.getPrivate(qbItemId),
        enabled: Boolean(qbItemId) && enabled,
        staleTime: 60 * 1000,
    });
}
export function useUpdateItemPrivate() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, data }) => catalog.updatePrivate(qbItemId, data),
        onSuccess: (_result, { qbItemId }) => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.itemPrivate(qbItemId) });
        },
    });
}
/**
 * QBO item price timeline (Manager+). Lazily fetched — pass the id only when the
 * history section is actually opened so viewing an item doesn't hit the network.
 */
export function useItemPriceHistory(qbItemId, enabled = true) {
    return useQuery({
        queryKey: catalogKeys.priceHistory(qbItemId ?? ''),
        queryFn: () => catalog.getPriceHistory(qbItemId),
        enabled: Boolean(qbItemId) && enabled,
        staleTime: 60 * 1000,
    });
}
/**
 * Explicit, audited write of an item's unit price to QuickBooks (source of
 * truth). On success, invalidate the catalog lists (so the displayed price
 * updates everywhere) and this item's price history (the write appends a row).
 */
export function useUpdateQboPrice() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, unitPrice }) => catalog.updateQboPrice(qbItemId, unitPrice),
        onSuccess: (_result, { qbItemId }) => {
            // ['catalog','items'] prefix also matches the catalog index (['catalog','items','all']).
            queryClient.invalidateQueries({ queryKey: ['catalog', 'items'] });
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
            queryClient.invalidateQueries({ queryKey: catalogKeys.stats });
            queryClient.invalidateQueries({ queryKey: catalogKeys.priceHistory(qbItemId) });
        },
    });
}
export function useUpdateItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, data, }) => catalog.updateItem(qbItemId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['catalog', 'items'] });
            queryClient.invalidateQueries({ queryKey: catalogKeys.stats });
        },
    });
}
export function useUploadThumbnail() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ qbItemId, formData }) => catalog.uploadThumbnail(qbItemId, formData),
        onSuccess: (result, { qbItemId }) => {
            const patch = {
                thumbnail_url: result.thumbnailUrl,
                thumbnail_storage_path: result.storagePath,
            };
            queryClient.setQueriesData({ queryKey: ['catalog', 'items'] }, (current) => (current?.map((item) => item.qb_item_id === qbItemId ? { ...item, ...patch } : item)));
            queryClient.setQueryData(catalogKeys.favorites, (current) => (current?.map((item) => item.qb_item_id === qbItemId ? { ...item, ...patch } : item)));
            queryClient.invalidateQueries({ queryKey: ['catalog', 'items'] });
            queryClient.invalidateQueries({ queryKey: catalogKeys.favorites });
            queryClient.invalidateQueries({ queryKey: catalogKeys.stats });
        },
    });
}
export function useCreateKit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => admin.createKit(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.kits });
        },
    });
}
export function useUpdateKit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ kitId, data, }) => admin.updateKit(kitId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.kits });
        },
    });
}
export function useDeleteKit() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (kitId) => admin.deleteKit(kitId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: catalogKeys.kits });
        },
    });
}
export function useDeleteThumbnail() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (qbItemId) => catalog.deleteThumbnail(qbItemId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['catalog', 'items'] });
            queryClient.invalidateQueries({ queryKey: catalogKeys.stats });
        },
    });
}
//# sourceMappingURL=useCatalog.js.map